// The site plan's pins from a job's records: every External area's photo on the
// visit, numbered and coloured as the report's cards are (@higgins/condition-library
// numberPins). Shared by the site plan page and the condition report's Report section,
// which says when the saved marked-up copy no longer shows them. Pure.
import { numberPins } from '@higgins/condition-library';
import { evidenceOf } from './condition-records';
import type { DeviceRecord } from './device-db';
import type { PlanPin } from './marked-up-plan';
import { planVersion, type SitePlanDoc } from './site-plan';
import { pinsFingerprint } from './site-plan-math';

const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** External areas' photos, numbered in area order then the order they were taken, coloured by area: the report numbers them the same (numberPins). */
export function pinsFor(records: DeviceRecord[]): { pin: PlanPin; label: string }[] {
  const areas = records.filter((r) => r.table === 'area').sort((a, b) => Number(a.fields.hig_sortorder ?? 0) - Number(b.fields.hig_sortorder ?? 0));
  const visitAreas = new Map(records.filter((r) => r.table === 'visitarea').map((v) => [v.id, v]));
  const areaOf = (visitAreaId: unknown) => String((typeof visitAreaId === 'string' && visitAreas.get(visitAreaId)?.fields.hig_measureareaid) || '');
  const evidence = new Map(evidenceOf(records).map((e) => [e.id, e]));
  const pins = numberPins(
    areas.map((a) => ({ id: a.id, side: typeof a.fields.hig_side === 'string' ? a.fields.hig_side : null })),
    [...evidence.values()].map((e) => ({ id: e.id, areaId: areaOf(e.fields.hig_visitareaid), takenOn: String(e.fields.hig_takenon ?? '') })),
  );
  return pins.map(({ id, areaId, number, colour }) => {
    const e = evidence.get(id) as DeviceRecord;
    return {
      pin: { id, number, colour, x: num(e.fields.hig_pinx), y: num(e.fields.hig_piny), bearing: num(e.fields.hig_bearing) },
      label: `${String(areas.find((a) => a.id === areaId)?.fields.hig_name ?? '')} · ${String(e.fields.hig_name ?? '').replace(/\.jpg$/i, '')}`,
    };
  });
}

/** Whether the plan's marked-up copy shows today's pins: none saved yet, current, or out of date. */
export function markedUpStatus(plan: SitePlanDoc, records: DeviceRecord[]): 'none' | 'current' | 'outOfDate' {
  if (!plan.markedUp) return 'none';
  return plan.markedUp.pins === pinsFingerprint(planVersion(plan), pinsFor(records).map((p) => p.pin)) ? 'current' : 'outOfDate';
}
